/**
 * The practice economy: XP, levels, Picks and streaks (plan doc 13).
 *
 * Its rules, in one place: nothing punishes a mistake — there are no lives,
 * no lockouts and no timers to wait out; one currency, Picks, is earned only
 * by practicing and spent only on the companion's wardrobe; a streak has a
 * rest day built in; and the social layer is a cooperative ensemble, not a
 * league. The column behind Picks is still called `gems` in the database and
 * in the sync payloads, so no data had to move; the product word is Picks.
 *
 * Pure functions, so the client can preview the reward on the completion screen
 * and the server can compute the authoritative value with the same code. The
 * client's figure is a preview only; the server's result wins.
 */

export * from './quests';
export * from './achievements';
export * from './ensembles';
export * from './chests';
export * from './bonds';
export * from './certificate';
export * from './username';

export const DAILY_GOALS = [10, 20, 30, 50] as const;
export type DailyGoal = (typeof DAILY_GOALS)[number];

/** The wardrobe is the whole shop: a cosmetic's price is its rarity's. */
export const PRICES = {
  cosmeticCommon: 150,
  cosmeticUncommon: 350,
  cosmeticRare: 700,
  cosmeticEpic: 1200,
} as const;

export const DAILY_XP_CAPS = {
  practice: 60,
  studio: 50,
  song: 60,
} as const;

/* -------------------------------------------------------------------- XP */

export interface SessionFacts {
  kind: 'lesson' | 'practice' | 'studio' | 'song' | 'placement' | 'checkpoint' | 'listening';
  exercises: number;
  correct: number;
  mistakes: number;
  maxCombo: number;
  accuracy: number;
  perfect: boolean;
  crownLevel?: number;
  legendary?: boolean;
  /** Mic exercises passed inside the session. */
  micPasses?: number;
  /** Stars earned, for songs. */
  stars?: number;
  firstCompletion?: boolean;
  /** XP already earned today in this category, for the daily caps. */
  earnedTodayInCategory?: number;
}

export interface XpBreakdown {
  base: number;
  perfectBonus: number;
  comboBonus: number;
  micBonus: number;
  crownMultiplier: number;
  /** After the multiplier and before the cap. */
  subtotal: number;
  cappedAt: number | null;
  total: number;
}

/** Combo bonus thresholds from the plan. */
function comboBonusFor(maxCombo: number): number {
  if (maxCombo >= 15) return 5;
  if (maxCombo >= 10) return 3;
  if (maxCombo >= 5) return 1;
  return 0;
}

export function xpFor(facts: SessionFacts): XpBreakdown {
  const crownLevel = facts.crownLevel ?? 1;
  const crownMultiplier =
    facts.legendary === true ? 1 : crownLevel >= 5 ? 1.5 : crownLevel >= 4 ? 1.25 : 1;

  let base: number;
  let perfectBonus = 0;
  let comboBonus = 0;
  let micBonus = 0;

  switch (facts.kind) {
    case 'lesson':
      if (facts.legendary === true) {
        base = 40;
      } else {
        base = 10;
        perfectBonus = facts.perfect ? 5 : 0;
        comboBonus = comboBonusFor(facts.maxCombo);
        micBonus = Math.min(5, facts.micPasses ?? 0);
      }
      break;
    case 'checkpoint':
      base = 50;
      break;
    case 'listening':
      base = 10;
      break;
    case 'placement':
      base = 0;
      break;
    case 'practice':
      base = facts.exercises >= 10 ? 5 : 0;
      perfectBonus = facts.accuracy >= 0.9 && facts.exercises >= 10 ? 5 : 0;
      break;
    case 'studio':
      base = 5;
      break;
    case 'song': {
      const stars = facts.stars ?? 0;
      base = stars * 5;
      perfectBonus =
        (facts.firstCompletion === true ? 5 : 0) +
        (stars === 3 && facts.firstCompletion === true ? 10 : 0);
      break;
    }
    default: {
      const never: never = facts.kind;
      throw new Error(String(never));
    }
  }

  const raw = base + perfectBonus + comboBonus + micBonus;
  const subtotal = Math.round(raw * crownMultiplier);

  // Daily caps stop grinding one activity; lessons are deliberately uncapped.
  const cap = dailyCapFor(facts.kind);
  const earned = facts.earnedTodayInCategory ?? 0;
  const allowed = cap === null ? subtotal : Math.max(0, Math.min(subtotal, cap - earned));
  const cappedAt = cap !== null && allowed < subtotal ? cap : null;

  return {
    base,
    perfectBonus,
    comboBonus,
    micBonus,
    crownMultiplier,
    subtotal,
    cappedAt,
    total: allowed,
  };
}

function dailyCapFor(kind: SessionFacts['kind']): number | null {
  if (kind === 'practice') return DAILY_XP_CAPS.practice;
  if (kind === 'studio') return DAILY_XP_CAPS.studio;
  if (kind === 'song') return DAILY_XP_CAPS.song;
  return null;
}

/* ----------------------------------------------------------------- level */

/** Cumulative XP needed to reach a level: 25 * L * (L + 1). */
export function xpToReach(level: number): number {
  if (level <= 1) return 0;
  const previous = level - 1;
  return 25 * previous * (previous + 1);
}

export function levelForXp(totalXp: number): number {
  if (totalXp <= 0) return 1;
  // Solve 25 * L * (L + 1) <= xp for the largest integer L, then add one.
  const level = Math.floor((-1 + Math.sqrt(1 + (4 * totalXp) / 25)) / 2) + 1;
  return Math.max(1, level);
}

export interface LevelProgress {
  level: number;
  xpIntoLevel: number;
  xpForLevel: number;
  fraction: number;
}

export function levelProgress(totalXp: number): LevelProgress {
  const level = levelForXp(totalXp);
  const start = xpToReach(level);
  const next = xpToReach(level + 1);
  const span = Math.max(1, next - start);
  return {
    level,
    xpIntoLevel: totalXp - start,
    xpForLevel: span,
    fraction: Math.max(0, Math.min(1, (totalXp - start) / span)),
  };
}

/* ----------------------------------------------------------------- picks */

/** Picks for a lesson that advanced a crown, and the bonus for a flawless one. */
export const CROWN_PICKS = 10;
export const PERFECT_CROWN_PICKS = 5;

/**
 * Mirrors the server's rule for the completion screen's preview. Only a crown
 * pays, so replaying a finished lesson earns XP and nothing to spend — the
 * wardrobe cannot be farmed.
 */
export function picksFor(facts: {
  kind: SessionFacts['kind'];
  crownAdvanced: boolean;
  perfect: boolean;
}): number {
  if (facts.kind !== 'lesson' && facts.kind !== 'checkpoint') return 0;
  if (!facts.crownAdvanced) return 0;
  return CROWN_PICKS + (facts.perfect ? PERFECT_CROWN_PICKS : 0);
}

/* --------------------------------------------------------------- streak */

/** A rest day comes round this often. */
export const REST_DAY_SPAN_DAYS = 7;

export interface StreakState {
  current: number;
  longest: number;
  /** Local date of the last day with activity, as YYYY-MM-DD. */
  lastDate: string | null;
  /** The last missed day that was forgiven as a rest day, as YYYY-MM-DD. */
  restUsedOn: string | null;
}

export interface StreakResult extends StreakState {
  extended: boolean;
  /** The missed day before this one was a rest day. */
  restUsed: boolean;
  lost: boolean;
}

function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Days between two YYYY-MM-DD dates, treating both as local calendar days. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) throw new Error(`Bad date: ${from} or ${to}`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Applies a day of activity to a streak.
 *
 * One missed day in any seven is a rest day: the streak carries on as if the
 * day had been played, and the missed day is remembered so the next rest day
 * is not due until a week after it. Two missed days in a row, or a second
 * missed day inside the week, start the streak again. Nothing can be bought,
 * held or equipped to change that; everyone gets the same day off.
 */
export function updateStreak(state: StreakState, localDate: string): StreakResult {
  const unchanged = { ...state, extended: false, restUsed: false, lost: false };

  if (state.lastDate === null) {
    return {
      ...state,
      current: 1,
      longest: Math.max(state.longest, 1),
      lastDate: localDate,
      extended: true,
      restUsed: false,
      lost: false,
    };
  }

  const gap = daysBetween(state.lastDate, localDate);

  // Same day: already counted. A date before the last recorded one is a
  // clock problem, not progress.
  if (gap <= 0) return unchanged;

  if (gap === 1) {
    const current = state.current + 1;
    return {
      current,
      longest: Math.max(state.longest, current),
      lastDate: localDate,
      restUsedOn: state.restUsedOn,
      extended: true,
      restUsed: false,
      lost: false,
    };
  }

  const missedDay = addDays(state.lastDate, 1);
  const restAvailable =
    state.restUsedOn === null || daysBetween(state.restUsedOn, missedDay) >= REST_DAY_SPAN_DAYS;
  if (gap === 2 && restAvailable) {
    const current = state.current + 1;
    return {
      current,
      longest: Math.max(state.longest, current),
      lastDate: localDate,
      restUsedOn: missedDay,
      extended: true,
      restUsed: true,
      lost: false,
    };
  }

  return {
    current: 1,
    longest: state.longest,
    lastDate: localDate,
    restUsedOn: state.restUsedOn,
    extended: true,
    restUsed: false,
    lost: true,
  };
}

export const STREAK_MILESTONES = [3, 7, 14, 30, 50, 100, 200, 365, 500, 1000] as const;

export function milestoneReached(before: number, after: number): number | null {
  for (const milestone of STREAK_MILESTONES) {
    if (before < milestone && after >= milestone) return milestone;
  }
  return null;
}

export const STREAK_MILESTONE_GEMS: Record<number, number> = {
  7: 50,
  14: 75,
  30: 200,
  50: 250,
  100: 500,
  200: 750,
  365: 1500,
};

/* ---------------------------------------------------------------- crowns */

/** Whether a session earned the next crown. */
export function earnsCrown(accuracy: number, currentCrowns: number): boolean {
  if (currentCrowns >= 5) return false;
  // The last crown is held to a higher standard.
  return currentCrowns === 4 ? accuracy >= 0.9 : accuracy >= 0.8;
}

/* ------------------------------------------------------------ anti-abuse */

/** Sessions too fast and too perfect to be real are excluded from leagues. */
export function looksAutomated(facts: {
  exercises: number;
  accuracy: number;
  medianResponseMs: number;
}): boolean {
  return facts.exercises >= 20 && facts.accuracy === 1 && facts.medianResponseMs < 700;
}

export const MAX_XP_PER_HOUR = 300;
export const MAX_SESSION_MINUTES = 90;
export const MAX_EXERCISES_PER_SESSION = 40;
